/**
 * Stage 0 — Upload Documents
 *
 * Pre-analysis screen. New matters land here; the drafter uploads
 * the IDF + any supplementary material before Stage 1's scoring +
 * Q&A pipeline runs. The Stage 0 tab stays accessible from any
 * later stage so supplementary documents can be added after Stage 1
 * has already been scored / frozen.
 *
 * Composition:
 *   <UploadZone />            — dropzone + vision toggle + progress
 *   <UploadedFilesList />     — full editable file rows (delete / retry)
 *   <ExtractedFiguresStatus /> — figures count summary
 *   <Continue or Frozen note> — depending on matter.current_stage
 *                                / stage_1.is_frozen
 *
 * Refresh wiring:
 *   UploadZone fires onUploaded after each successful file POST.
 *   We bump filesRefreshKey + figuresRefreshKey from there so the
 *   list and the figures status refetch immediately rather than
 *   waiting for their internal poll cadence. We also call
 *   refreshMatter so the matter blob (idf_documents, completeness
 *   scores) stays current — matters re-entering Stage 0 from a
 *   frozen Stage 1 still benefit from a current matter snapshot.
 */
import React, { useEffect, useState } from 'react';
import {
  ArrowRight, Loader2, AlertTriangle,
  FileText, File, FileSpreadsheet, FileCode, Mail, FileImage,
  ChevronDown, ChevronRight,
} from 'lucide-react';

import { useAppStore } from '@/store';
import * as api from '@/api/client';
import UploadZone from '@/components/upload/UploadZone';
import UploadedFilesList from '@/components/upload/UploadedFilesList';
import PasteTextPanel from '@/components/upload/PasteTextPanel';
import ExtractedFiguresStatus from '@/components/upload/ExtractedFiguresStatus';


import { apiErrorText } from '../../lib/describeApiError';
export default function Stage0() {
  const { matterState, setMatterState, setActiveStage } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';

  // Stage 1's is_frozen flag drives:
  //   - The "Rescore to incorporate" reminder under the upload zone
  //   - Backend refuses delete / retry while frozen, so we hide
  //     those affordances in UploadedFilesList too
  const stage1Frozen = !!matterState?.stages?.stage_1?.is_frozen;

  // Refresh tokens — bumped after every successful file upload so
  // UploadedFilesList + ExtractedFiguresStatus refetch out of band.
  // Each is a separate counter so a future caller can refetch one
  // without the other; today they always move together.
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [figuresRefreshKey, setFiguresRefreshKey] = useState(0);

  // (document_id → job_id) map for async-path uploads. Populated by
  // UploadZone's onJobIdMapped callback when a POST /idf returns
  // 202; UploadedFilesList reads it to drive per-row step-progress
  // polling against /api/jobs/{job_id}/status.
  //
  // OPT-3 — persisted under sessionStorage(`job_map_${matterId}`)
  // so a page reload mid-upload doesn't collapse the per-row step
  // progress to the generic "Processing…" chip. Scoped to matterId
  // so switching matters can't bleed state. Falls back to an empty
  // map silently when sessionStorage is unavailable (private
  // browsing, SSR pre-hydration).
  const jobMapStorageKey = matterId ? `job_map_${matterId}` : '';
  const [jobIdsByDocument, setJobIdsByDocument] = useState<
    Record<string, string>
  >(() => {
    if (typeof window === 'undefined' || !jobMapStorageKey) return {};
    try {
      const stored = window.sessionStorage.getItem(jobMapStorageKey);
      if (!stored) return {};
      const parsed = JSON.parse(stored);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch { return {}; }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !jobMapStorageKey) return;
    try {
      window.sessionStorage.setItem(
        jobMapStorageKey, JSON.stringify(jobIdsByDocument),
      );
    } catch { /* private mode / quota — fall through */ }
  }, [jobIdsByDocument, jobMapStorageKey]);

  // OPT-1 — optimistic placeholder rows. UploadZone fires
  // onUploadStart before the POST dispatches; we register a
  // pending entry; UploadedFilesList renders it at the top of
  // the list with a spinner + "Uploading…" badge. onUploadSettled
  // removes the entry on success (real row appears via refetch)
  // or failure (with an inline error toast).
  const [pendingFiles, setPendingFiles] = useState<
    Array<{ tempId: string; filename: string; size: number }>
  >([]);
  const [uploadFailures, setUploadFailures] = useState<
    Array<{ tempId: string; filename: string; error?: string }>
  >([]);

  // OPT-4 — surface refresh errors as a non-intrusive amber notice
  // instead of swallowing them silently. Auto-clears after 8s so
  // a transient blip doesn't linger.
  const [refreshError, setRefreshError] = useState(false);

  // Track the live document list so we can pass docCount to
  // ExtractedFiguresStatus (its useEffect re-runs on docCount
  // change, picking up newly-extracted figures from a fresh
  // upload). The list owns the polling itself; we just observe.
  const [docCount, setDocCount] = useState<number>(0);
  const [indexedCount, setIndexedCount] = useState<number>(0);

  // Build a name → legacy idf_document map so UploadedFilesList can
  // surface char count + per-file warnings on primary IDF rows.
  // Recomputed whenever the matter blob changes; cheap.
  const legacyDocsByName = React.useMemo(() => {
    const m = new Map<string, { extraction_warnings?: string[]; extracted_text?: string }>();
    for (const d of (matterState?.idf_documents || []) as any[]) {
      m.set(d.filename, {
        extraction_warnings: d.extraction_warnings,
        extracted_text: d.extracted_text,
      });
    }
    return m;
  }, [matterState?.idf_documents]);

  const refreshMatter = React.useCallback(async () => {
    if (!matterId) return;
    try {
      const fresh = await api.getMatter(matterId);
      setMatterState(fresh);
    } catch (err) {
      // OPT-4 — surface failure as a tiny amber notice rather
      // than swallowing it. Non-intrusive; nothing is blocked.
      // eslint-disable-next-line no-console
      console.warn('[Stage0] refreshMatter failed:', err);
      setRefreshError(true);
      window.setTimeout(() => setRefreshError(false), 8000);
    }
  }, [matterId, setMatterState]);

  const handleUploaded = React.useCallback(() => {
    // OPT-2 — two-shot list refetch. The immediate bump catches
    // the just-INSERTed matter_documents row; the 1.5s follow-up
    // catches the async-queue write that flips status when the
    // worker has had a moment to pick the job up. Both fall
    // through UploadedFilesList's refreshKey effect, so they
    // share the same dedup / cancellation guard the polling path
    // already has.
    setFilesRefreshKey((k) => k + 1);
    setFiguresRefreshKey((k) => k + 1);
    window.setTimeout(() => {
      setFilesRefreshKey((k) => k + 1);
    }, 1500);
    refreshMatter().catch(() => { /* refreshMatter handles its own surfacing */ });
  }, [refreshMatter]);

  // OPT-1 — register / settle placeholder rows for the optimistic
  // file list. tempId is generated by UploadZone before the POST
  // fires so the row appears in the same tick as the drop.
  const handleUploadStart = React.useCallback(
    (entry: { tempId: string; filename: string; size: number }) => {
      setPendingFiles((prev) => [...prev, entry]);
    }, [],
  );
  const handleUploadSettled = React.useCallback(
    (tempId: string, outcome: { ok: boolean; filename: string; error?: string }) => {
      setPendingFiles((prev) => prev.filter((p) => p.tempId !== tempId));
      if (!outcome.ok) {
        const entry = { tempId, filename: outcome.filename, error: outcome.error };
        setUploadFailures((prev) => [...prev, entry]);
        // Auto-dismiss the inline failure toast after 10s.
        window.setTimeout(() => {
          setUploadFailures((prev) => prev.filter((f) => f.tempId !== tempId));
        }, 10_000);
      }
    }, [],
  );

  // ───── Continue / advance flow ────────────────────────────────
  // Only visible when current_stage is still stage_0. Re-entries
  // from stage_1+ replace this with the frozen-stage-1 reminder
  // (or nothing, if stage_1 isn't frozen).
  const onStage0 = matter?.current_stage === 'stage_0';
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState<string | null>(null);

  const canAdvance = onStage0 && indexedCount > 0 && !advancing;
  const disabledReason = onStage0 && indexedCount === 0
    ? 'Upload and process at least one document first'
    : '';

  const handleAdvance = async () => {
    if (!matterId || advancing || !onStage0) return;
    setAdvancing(true);
    setAdvanceError(null);
    try {
      await api.advanceStage0(matterId);
      // Re-fetch BEFORE flipping activeStage so TopBar's currentIdx
      // (driven by matter.current_stage) reflects the promotion;
      // otherwise Stage 1 would render but its tab would still
      // look locked.
      const fresh = await api.getMatter(matterId);
      setMatterState(fresh);
      setActiveStage('stage_1');
    } catch (e: any) {
      setAdvanceError(apiErrorText(e, 'Could not advance to Stage 1.'));
    } finally {
      setAdvancing(false);
    }
  };

  if (!matter) return null;

  return (
    <div
      data-testid="stage-0"
      className="max-w-4xl mx-auto pt-16 pb-12 px-6"
    >
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Stage 0
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-zinc-900"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Upload Documents
        </h1>
        <p className="text-sm text-zinc-600 mt-1 leading-relaxed max-w-2xl">
          Drop in your invention disclosure and any supporting
          material. Stage 1 starts as soon as the first file is
          ready; you can keep adding more later.
        </p>
        <p
          className="text-xs text-zinc-500 mt-1 leading-relaxed max-w-2xl"
          data-testid="stage-0-processing-hint"
        >
          Each file is read, indexed and classified automatically. Files added
          after Stage 1 has been analysed (for example the inventor&apos;s
          answers) are only used once you click <strong>Rescore</strong> in
          Stage 1 — that re-reads every document and refreshes the summary,
          scores and Technology Basics.
        </p>
      </div>

      {/* Stage-1-frozen reminder. Only meaningful for re-entries
          (current_stage > stage_0 AND stage_1 is frozen). Tells the
          drafter that newly-uploaded docs won't be picked up by the
          locked-in scoring until they Rescore. */}
      {!onStage0 && stage1Frozen && (
        <div
          data-testid="stage-0-frozen-note"
          className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <span>
            Stage 1 is frozen. New documents will not be included in
            the analysis until you click Rescore in Stage 1.
          </span>
        </div>
      )}

      <CompatibleSourcesPanel />

      <div className="space-y-6">
        <UploadZone
          matterId={matterId}
          onUploaded={handleUploaded}
          onJobIdMapped={(documentId, jobId) =>
            setJobIdsByDocument((prev) => ({ ...prev, [documentId]: jobId }))
          }
          onUploadStart={handleUploadStart}
          onUploadSettled={handleUploadSettled}
        />

        {/* Paste-as-document surface. Companion to the dropzone:
            same onUploaded callback, same upload pipeline (synthetic
            ``.txt`` for the body + one standalone-image upload per
            pasted image). Fully collapsed by default so it doesn't
            steal real estate from the primary dropzone. */}
        <PasteTextPanel
          matterId={matterId}
          onUploaded={handleUploaded}
        />

        {/* OPT-1 — inline failure toast for optimistic uploads
            that bounced. Auto-dismisses after 10s; clicking the
            × dismisses early. */}
        {uploadFailures.length > 0 && (
          <div
            data-testid="stage-0-upload-failures"
            className="space-y-1.5"
          >
            {uploadFailures.map((f) => (
              <div
                key={f.tempId}
                className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-start gap-2"
              >
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0 text-red-600" />
                <span className="flex-1 min-w-0">
                  Upload failed: <strong className="font-medium">{f.filename}</strong>
                  {f.error ? <span className="ml-1 text-red-700">— {f.error}</span> : null}
                </span>
                <button
                  type="button"
                  onClick={() => setUploadFailures((prev) =>
                    prev.filter((x) => x.tempId !== f.tempId),
                  )}
                  className="text-red-700 hover:text-red-900 text-[10px] font-medium"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* OPT-4 — refresh-error amber notice. Sits above the
            file list so the drafter can see "page may be stale"
            without it blocking any action. Auto-clears after 8s
            via the timeout in refreshMatter's catch block. */}
        {refreshError && (
          <p
            data-testid="stage-0-refresh-error"
            className="text-xs text-amber-700 -mt-3"
          >
            Page data may be momentarily stale — refreshing…
          </p>
        )}

        <UploadedFilesList
          matterId={matterId}
          mode="editable"
          refreshKey={filesRefreshKey}
          isFrozen={stage1Frozen}
          legacyDocsByName={legacyDocsByName}
          jobIdsByDocument={jobIdsByDocument}
          pendingFiles={pendingFiles}
          onDocumentsChange={(docs) => {
            setDocCount(docs.length);
            setIndexedCount(docs.filter((d) => d.status === 'indexed').length);
            // OPT-3 — once a doc reaches a terminal status, drop
            // its entry from the (document_id → job_id) map (and
            // therefore from sessionStorage on the next effect
            // tick). Keeps the persisted map bounded and avoids
            // a finished job's id sticking around for the whole
            // session.
            const terminalIds = new Set(
              docs
                .filter((d) => d.status === 'indexed' || d.status === 'failed')
                .map((d) => String(d.id)),
            );
            if (terminalIds.size === 0) return;
            setJobIdsByDocument((prev) => {
              let changed = false;
              const next: Record<string, string> = {};
              for (const [k, v] of Object.entries(prev)) {
                if (terminalIds.has(k)) { changed = true; continue; }
                next[k] = v;
              }
              return changed ? next : prev;
            });
          }}
        />

        {/*
          ExtractedFiguresStatus hides itself when docCount is 0 or
          when no figures have been extracted yet, so it's safe to
          mount unconditionally. refreshKey forces a refetch after
          each successful upload even when docCount hasn't shifted
          (e.g. a re-upload of an existing filename).
        */}
        <ExtractedFiguresStatus
          matterId={matterId}
          docCount={docCount}
          refreshKey={figuresRefreshKey}
        />
      </div>

      {advanceError && (
        <div
          data-testid="stage-0-error"
          className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2"
        >
          <AlertTriangle size={14} /> {advanceError}
        </div>
      )}

      {onStage0 && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            data-testid="stage-0-continue-button"
            onClick={handleAdvance}
            disabled={!canAdvance}
            title={disabledReason || undefined}
            className={
              'inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ' +
              (canAdvance
                ? 'bg-[#002FA7] text-white hover:bg-[#002277]'
                : 'bg-zinc-200 text-zinc-500 cursor-not-allowed')
            }
          >
            {advancing
              ? <Loader2 size={14} className="animate-spin" />
              : <ArrowRight size={14} />}
            Continue to Analysis
          </button>
        </div>
      )}
    </div>
  );
}


// ─── Compatible sources info panel ───────────────────────────────
// Discreet expandable card listing the file formats the upload
// pipeline can ingest. Mirrors the per-format icon mapping used in
// UploadedFilesList so a drafter glancing at the dropzone sees
// the same visual language as a row that's already been
// processed. Collapsed by default; one click to expand.

interface SourceGroup {
  label: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  iconClass: string;
  formats: string[];
}

const COMPATIBLE_SOURCES: SourceGroup[] = [
  {
    label: 'Documents',
    Icon: FileText,
    iconClass: 'text-blue-500',
    formats: ['PDF', 'DOCX', 'PPTX', 'XLSX', 'TXT'],
  },
  {
    label: 'Text & markup',
    Icon: FileCode,
    iconClass: 'text-violet-600',
    formats: ['Markdown (.md)', 'HTML (.html, .htm)', 'RTF', 'JSON', 'XML'],
  },
  {
    label: 'Tabular',
    Icon: FileSpreadsheet,
    iconClass: 'text-emerald-600',
    formats: ['CSV', 'TSV', 'XLSX'],
  },
  {
    label: 'Email',
    Icon: Mail,
    iconClass: 'text-amber-600',
    formats: ['EML (body + headers; inline images extracted)'],
  },
  {
    label: 'Images',
    Icon: FileImage,
    iconClass: 'text-orange-500',
    formats: ['PNG', 'JPG', 'GIF', 'TIF', 'BMP', 'WEBP'],
  },
];

function CompatibleSourcesPanel(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="mb-4 rounded-sm border border-zinc-200 bg-white"
      data-testid="stage-0-compatible-sources"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-zinc-50"
        aria-expanded={open}
        data-testid="stage-0-compatible-sources-toggle"
      >
        <span className="flex items-center gap-2 text-xs text-zinc-600">
          <File size={14} className="text-zinc-400" />
          <span className="font-medium text-zinc-700">
            Compatible sources
          </span>
          <span className="text-zinc-400">
            — PDF / DOCX / PPTX / XLSX / TXT / Markdown / HTML / RTF / CSV / TSV / JSON / XML / EML / images
          </span>
        </span>
        {open ? (
          <ChevronDown size={14} className="text-zinc-400 flex-shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-zinc-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div
          className="border-t border-zinc-200 px-3 py-2 space-y-1.5"
          data-testid="stage-0-compatible-sources-detail"
        >
          {COMPATIBLE_SOURCES.map((g) => (
            <div key={g.label} className="flex items-start gap-2 text-xs">
              <g.Icon size={14} className={`${g.iconClass} mt-0.5 flex-shrink-0`} />
              <div className="flex-1">
                <span className="font-medium text-zinc-700">{g.label}: </span>
                <span className="text-zinc-600">{g.formats.join(', ')}</span>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-zinc-500 pt-1">
            Max file size: 50 MB. Inline images in HTML and email
            bodies are extracted to the figures registry
            automatically. CSV and TSV are capped at the first 500
            data rows for the LLM prompt.
          </p>
        </div>
      )}
    </div>
  );
}
